import dayjs from "dayjs";

/**
 * Cinematic universe releases (movies / show seasons) synced from TMDB by
 * the API. Maps the franchise tabs onto the API franchise slugs.
 * Mirrors the mobile app's features/releases/helpers/releases.js.
 */
export const RELEASE_FRANCHISES = [
  { key: "marvel", slug: "mcu", franchise: 1, label: "Marvel" },
  { key: "dc", slug: "dcu", franchise: 2, label: "DC" },
  { key: "starwars", slug: "star_wars", franchise: 3, label: "Star Wars" },
  { key: "image", slug: null, franchise: 4, label: "Image" }, // no releases
];

export const releasesQueryKey = (slug) => ["screenReleases", slug];
export const releaseQueryKey = (id) => ["screenRelease", String(id)];
export const releasesQueryOptions = { staleTime: 5 * 60 * 1000 };

const parseDate = (value) => {
  if (!value) {
    return null;
  }
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

export const formatReleaseDate = (value) => {
  const date = parseDate(value);
  return date ? date.format("D MMM YYYY") : "";
};

export const isMovie = (release) => release?.type === "movie";

/**
 * "Film · 18 Dec 2026" or "Season 2 · 24 Mar 2026"
 */
export const formatReleaseMeta = (release) => {
  if (!release) {
    return "";
  }

  const kind = isMovie(release)
    ? "Film"
    : `Season ${Number(release.seasonNumber) || 1}`;

  return [kind, formatReleaseDate(release.releaseDate)]
    .filter(Boolean)
    .join(" · ");
};

export const hasReadingOrder = (release) =>
  Array.isArray(release?.readingOrders) && release.readingOrders.length > 0;

/**
 * Normalise the API payload ({data: {upcoming: [], released: []}}).
 */
export const getReleaseSections = (payload) => {
  const pick = (key) =>
    Array.isArray(payload?.data?.[key])
      ? payload.data[key].filter((item) => item && typeof item === "object")
      : [];

  return { upcoming: pick("upcoming"), released: pick("released") };
};

/**
 * Countdown / availability chip shown on the detail page.
 * Upcoming: "Today", "Tomorrow", "In 12 days", or the date when far away.
 * Released: "Airing now" while a season is still airing, otherwise "Released".
 */
export const getAvailability = (release, today = new Date()) => {
  const releaseDate = parseDate(release?.releaseDate);
  if (!releaseDate) {
    return null;
  }

  const days = releaseDate
    .startOf("day")
    .diff(dayjs(today).startOf("day"), "day");
  if (days > 0 || (days === 0 && release?.isUpcoming)) {
    if (days === 0) {
      return { label: "Today", colorScheme: "red" };
    }
    if (days === 1) {
      return { label: "Tomorrow", colorScheme: "red" };
    }
    if (days <= 60) {
      return { label: `In ${days} days`, colorScheme: "red" };
    }
    return {
      label: formatReleaseDate(release.releaseDate),
      colorScheme: "red",
    };
  }

  const endDate = parseDate(release?.endDate);
  if (
    !isMovie(release) &&
    endDate &&
    endDate.startOf("day").diff(dayjs(today).startOf("day"), "day") >= 0
  ) {
    return { label: "Airing now", colorScheme: "green" };
  }

  return { label: "Released", colorScheme: "green" };
};

/**
 * TMDB production status → chip label and Chakra colour scheme.
 */
export const getStatusChip = (status) => {
  if (!status) {
    return null;
  }

  const normalised = String(status).toLowerCase();
  const label = (normalised[0].toUpperCase() + normalised.slice(1)).replace(
    "Post production",
    "Post-production"
  );

  if (
    ["planned", "in production", "post production", "rumored"].includes(
      normalised
    )
  ) {
    return { label, colorScheme: "orange" };
  }
  if (normalised === "returning series") {
    return { label, colorScheme: "blue" };
  }
  if (normalised === "canceled") {
    return { label, colorScheme: "red" };
  }

  return { label, colorScheme: "gray" };
};

/**
 * Label above the linked reading order(s) on the detail page.
 */
export const getReadingOrderLabel = (release, count = 1) => {
  if (release?.isUpcoming) {
    return count > 1 ? "READ BEFORE THEY LAND" : "READ BEFORE IT LANDS";
  }
  return count > 1 ? "READING ORDERS" : "READING ORDER";
};

/**
 * "18 of 52" progress copy for a reading of a linked order.
 */
export const formatReadingProgress = (reading) => {
  if (!reading) {
    return "";
  }
  const read = Number(reading.totalComicsRead ?? reading.readCount) || 0;
  const total =
    Number(reading.totalComics ?? reading.readingOrder?.comicsCount) || 0;
  return total ? `${read} of ${total}` : `${read} read`;
};
